import React, { useState } from 'react';
import type { Currency } from '../types';
import { useUpload } from '../lib/useUpload';
import { Modal, Alert, UploadProgress, inputClass, labelClass, primaryBtn, secondaryBtn } from './ui';

const EXPENSE_CATEGORIES = [
  'Fuel',
  'Electricity',
  'Repairs',
  'Maintenance',
  'Cleaning',
  'Security',
  'Utilities',
  'Transport',
  'Stationery',
  'Marketing',
  'Staff Welfare',
  'Other',
];

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}

export default function AddExpenseModal({ open, onClose, onSubmitted }: Props) {
  const [form, setForm] = useState({
    category: EXPENSE_CATEGORIES[0],
    description: '',
    amount: '',
    currency: 'NGN' as Currency,
    expense_date: new Date().toISOString().slice(0, 10),
  });
  const [receipt, setReceipt] = useState<File | null>(null);
  const upload = useUpload();
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    upload.reset();
    setError('');
    try {
      const fd = new FormData();
      fd.append('category', form.category);
      if (form.description) fd.append('description', form.description);
      fd.append('amount', form.amount);
      fd.append('currency', form.currency);
      fd.append('expense_date', form.expense_date);
      if (receipt) fd.append('receipt', receipt);
      await upload.run('/expenses', fd);
      onSubmitted();
      onClose();
      setForm({
        category: EXPENSE_CATEGORIES[0],
        description: '',
        amount: '',
        currency: 'NGN',
        expense_date: new Date().toISOString().slice(0, 10),
      });
      setReceipt(null);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Expense">
      {error && <Alert>{error}</Alert>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Category *</label>
            <select className={inputClass} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Expense Date</label>
            <input type="date" className={inputClass} value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Amount *</label>
            <input type="number" step="0.01" min="0" required className={inputClass} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>Currency</label>
            <select className={inputClass} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value as Currency })}>
              <option value="NGN">NGN</option>
              <option value="USD">USD</option>
            </select>
          </div>
        </div>
        <div>
          <label className={labelClass}>Description</label>
          <textarea className={inputClass} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What was the expense for?" />
        </div>
        <div>
          <label className={labelClass}>Receipt (image, PDF or Excel)</label>
          <input type="file" className={inputClass} accept="image/jpeg,image/png,image/gif,image/webp,.pdf,.xls,.xlsx,.csv" onChange={(e) => setReceipt(e.target.files?.[0] || null)} />
        </div>
        <UploadProgress
          percent={upload.percent}
          phase={upload.phase}
          error={upload.error}
          onCancel={upload.cancel}
        />
        <div className="flex justify-end space-x-3">
          <button type="button" onClick={onClose} className={secondaryBtn}>Cancel</button>
          <button type="submit" disabled={upload.busy} className={primaryBtn}>
            {upload.phase === 'uploading'
              ? `Uploading ${upload.percent}%`
              : upload.phase === 'processing' ? 'Saving...' : 'Add Expense'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
